import React, { useCallback, useEffect, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import TableSkeleton from "../../../components/Skeleton";

import {
    createCertificateEntity,
    getAllCertificateEntities,
    updateCertificateEntity,
} from "../../../redux/features/certificateEntity/certificateEntityService";
import {
    STATUS,
    selectCertificateList,
    selectUpdateCertificateEntity,
} from "../../../redux/features/certificateEntity/certificateEntitySlice";
import Modal from "../../../components/Modal";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";

const CertificateEntity = () => {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        name: '',
        code: '',
    });
    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setDisabledBtn] = useState(false);
    const [skip, setSkip] = useState(0);
    const [limit] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [initialEntity, setInitialEntity] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        code: ''
    });

    const [errorMsg, setErrorMsg] = useState(null);
    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);

    const { status, data: certificateEntities, pageCount } = useSelector(selectCertificateList);
    const isLoading = status === STATUS.LOADING;
    
    const { status: updateStatus, message: updateMsg } = useSelector(selectUpdateCertificateEntity);
    const isUpdating = updateStatus === STATUS.LOADING;

    const fetchAllCertificateEntities = useCallback(() => {
        dispatch(getAllCertificateEntities({
            skip,
            limit,
            search: searchQuery,
        }));
    }, [dispatch, skip, limit, searchQuery]);

    useEffect(() => {
        fetchAllCertificateEntities();
    }, [fetchAllCertificateEntities]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrorMessages((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = "Name is required.";
        if (!formData.code.trim()) {
            errors.code = "Code is required.";
        }
        setErrorMessages(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateCertificateEntity = async () => {
        if (!validateForm()) return;
        setDisabledBtn(true);
        try {
            const response = await dispatch(createCertificateEntity(formData)).unwrap();
            showToast("success", 'Certificate entity created successfully.');
            setFormData({ name: '', code: '' });
            fetchAllCertificateEntities();
        } catch (error) {
            showToast("error", error.message);
        } finally {
            setDisabledBtn(false);
        }
    };

    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    // Status update
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedEntityId, setSelectedEntityId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(updateCertificateEntity({ id: selectedEntityId, status: true })).unwrap();
            setSelectedEntityId(null);
            setInitialEntity(null);
            showToast('success', response.message);
            fetchAllCertificateEntities();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(updateCertificateEntity({ id: selectedEntityId, status: false })).unwrap();
            setSelectedEntityId(null);
            setInitialEntity(null);
            showToast('success', response.message);
            fetchAllCertificateEntities();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Edit
        const handleSelectedEntityChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedEntity((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

     const isValidEditEntity = () => {
        const errors = {};
        const trimmedName = selectedEntity.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!selectedEntity.code) {
            errors.code = "Code is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialEntity[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

     const editEntityDetails = async () => {
            if (isValidEditEntity(selectedEntity)) {
                const data = getChangedValues(selectedEntity);
                const hasChanges = Object.keys(data).some(key => key !== 'id');
                if (!hasChanges) {
                    setErrorMsg('There are no changes to update.');
                } else {
                    setEditBtnDisable(true);
                    try {
                        const response = await dispatch(updateCertificateEntity(data)).unwrap();
                        setEditBtnDisable(false);
                        setSelectedEntity(null);
                        setInitialEntity(null);
                        setModalOpen(false);
                        showToast('success', response.message);
                        fetchAllCertificateEntities();
                    }
                    catch (error) {
                        setEditBtnDisable(false);
                        showToast('error', error.message);
                    };
                }
            }
        };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Certificate Entity</h1>
                <div className={styles.formContainer}>
                    <form className={styles.coordinatorForm} noValidate>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name || ''}
                                        onChange={handleInputChange}
                                        placeholder='Enter Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='email' className={`form-label`}>
                                        Code<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='code'
                                        name='code'
                                        value={formData.code || ''}
                                        onChange={handleInputChange}
                                        placeholder='Enter Code'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.code && (
                                        <p className={`text-danger`}>{errorMessages.code}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={handleCreateCertificateEntity}
                        >
                            {disabledBtn ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Certificate Entities</h1>
                    <SearchBar onSearch={(query) => setSearchQuery(query)} />
                </div>
                <div className={`search-card-body`}>
                    {isLoading ? (
                        <TableSkeleton
                            columns={["Name", "Code", "Status", "Actions"]}
                            rowCount={10}
                            height={20}
                        />
                    ) : certificateEntities.length > 0 ? (
                        <div className={`table-container`}>
                            <table className={`table`}>
                                <thead className={`thead`}>
                                    <tr>
                                        <th className={`theadColumnOne`}>Name</th>
                                        <th>Code</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody className={`tbody`}>
                                    {certificateEntities.map((entity) => (
                                        <tr key={entity.id}>
                                            <td className={`tdataColumnOne`}>{entity.name}</td>
                                            <td>{entity.code}</td>
                                            <td>{entity?.status ? 'Active' : 'Inactive'}</td>
                                            <td className={`actionsBtn`}>
                                                <button className={`btn-edit-label`} onClick={() => {
                                                    setSelectedEntity(entity);
                                                    setInitialEntity(entity);
                                                    setEditBtnDisable(false);
                                                    setErrorMsg(null);
                                                    setEditErrorMsgs({
                                                        name: '',
                                                        code: ''
                                                    })
                                                    setModalOpen(true);
                                                }}>Edit</button>
                                                <button
                                                    className={entity.status ? `btn-inactive` : `btn-active`}
                                                    onClick={() => {
                                                        setSelectedEntityId(entity.id);
                                                        entity.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                    }
                                                    }>{entity.status ? 'Deactivate' : 'Acitvate'}</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <NoResultsFound />
                    )}
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Update Entity Details</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='full_name'
                                                name='full_name'
                                                value={selectedEntity.name || ''}
                                                onChange={handleSelectedEntityChange}
                                                placeholder='Enter StaffMember Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Code<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='code'
                                                name='code'
                                                value={selectedEntity.code || ''}
                                                onChange={handleSelectedEntityChange}
                                                placeholder='Enter Code'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.code && (
                                                <p className={`text-danger`}>{editErrorMsgs.code}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editEntityDetails}
                                    >
                                        {isUpdating? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Certificate Entity Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the certificate entity?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Certificate Entity Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the certificate entity?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default CertificateEntity;